import {Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnDestroy, OnInit} from '@angular/core';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {combineLatest, filter, iif, Observable, of, ReplaySubject, Subscription, switchMap, take} from 'rxjs';
import {ErrorInfo} from '../../shared/model/error-info';
import {DeliveryService} from '../../shared/service/delivery/delivery.service';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {DeliveryType} from '../../shared/model/delivery-type';
import {Platform} from '@angular/cdk/platform';
import {NumberProductsPipe} from '../../shared/pipes/number-products.pipe';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {MatCard} from '@angular/material/card';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {AsyncPipe, DatePipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {DacklAccordionComponent, DacklExpansionPanelComponent} from '@datev/ngx-dackl';
import {MatIcon} from '@angular/material/icon';
import {MatAnchor, MatFabButton} from '@angular/material/button';
import {PageState, PageStateService} from '../../shared/service/page-state/page-state.service';
import {DeliveryDescriptionsByDateCategory} from '../../shared/model/deliveryDescriptionsByDateCategory';
import {MatSlideToggle, MatSlideToggleChange} from '@angular/material/slide-toggle';
import {AppStateService} from 'commons';
import {catchError, map} from 'rxjs/operators';
import {PersonalService} from '../../shared/service/personal/personal.service';
import {NewsService} from '../../shared/service/news/news.service';
import {AuthenticationService, UserInfo} from '@datev/ngx-dcal';
import {BusinessPartner} from '../../shared/model/business-partner';
import {InformationScope} from '../../shared/model/information-scope';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {NewsItem} from '../../shared/model/news-item';
import {AnnouncementComponent} from '../announcement/announcement.component';
import {ChangeCategoriesComponent} from '../change-categories/change-categories.component';
import {DeliveryItemsTableViewComponent} from '../delivery-items-table-view/delivery-items-table-view.component';

import '@datev/dacks/content';
import '@datev/dacks/content-header';
import { FooterSpacing } from '../footer/footer-spacing';


export interface DeliveryAllItemsPageState extends PageState {
  expandedPanelId: string;
  isPersonalFilter: boolean;
}

@Component({
    selector: 'myupdates-delivery-all-items',
    templateUrl: './delivery-all-items.component.html',
    styleUrls: ['./delivery-all-items.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [MatIcon, MatAnchor, NgClass, RouterLink, MatCard,
    ErrorBoxComponent, MatProgressSpinner, AsyncPipe, DatePipe, NumberProductsPipe, TranslatePipe,
    NgTemplateOutlet, DacklAccordionComponent, DacklExpansionPanelComponent, MatSlideToggle, MatFabButton, ChangeCategoriesComponent,
    DeliveryItemsTableViewComponent, FooterSpacing]
})
export class DeliveryAllItemsComponent implements OnInit, OnDestroy {

  private readonly dialog = inject(MatDialog);

  deliveryDescriptionsByDateCategory$?: Observable<DeliveryDescriptionsByDateCategory>;
  errorInfoForDeliveries$ = new ReplaySubject<ErrorInfo>(1);
  errorInfoForNews$ = new ReplaySubject<ErrorInfo>(1);
  newsItems$?: Observable<NewsItem[]>;

  private personalizeSubscription?: Subscription;
  private personalizeNewsSubscription?: Subscription;
  private loginSubscription?: Subscription;
  private routeSubscription?: Subscription;
  protected readonly informationScope = InformationScope;

  dateTranslation = '';
  dateTranslationMonth = '';
  welcomeGeneral = '';
  welcomePersonal = '';
  displayWelcome = '';

  protected readonly DeliveryType = DeliveryType;
  pageState = {} as DeliveryAllItemsPageState;

  constructor(private readonly router: Router,
              private readonly route: ActivatedRoute,
              private readonly deliveryService: DeliveryService,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly translate: TranslateService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly newsService: NewsService,
              private readonly auth: AuthenticationService,
              private readonly personalService: PersonalService,
              private readonly pageStateService: PageStateService,
              readonly appState: AppStateService,
              public platform: Platform) {
  }

  ngOnInit(): void {
    this.dateTranslation = this.translate.instant('common.date_format');
    this.dateTranslationMonth = this.translate.instant('common.date_format_month');
    this.welcomeGeneral = this.translate.instant('common.title_welcome');
    this.welcomePersonal = this.translate.instant('common.title_welcome_personal');

    this.routeSubscription = this.router.events.pipe(
      filter(ev => ev instanceof NavigationEnd)
    ).subscribe(() => {
      this.checkRouteForNewsItemsId();
    });
    this.checkRouteForNewsItemsId();

    this.personalizeNewsSubscription = this.appState.isPersonalized$.pipe(
      switchMap(isPersonalized =>
        iif(() => isPersonalized,
          this.personalService.activeBusinessPartner$,
          of<BusinessPartner>( this.personalService.invalidBusinessPartner )) )
    ).subscribe(businessPartner => {
      this.newsItems$ = this.getNewsItems(businessPartner.businessPartnerId !== 0);
    });

    this.loginSubscription = this.appState.isPersonalized$.subscribe(isPersonalized => {
      if (isPersonalized) {
        this.displayUserNameFromAuth();
      } else {
        this.displayWelcome = this.welcomeGeneral;
      }
    });

    this.personalizeSubscription = this.appState.isPersonalized$.pipe(
      switchMap(isPersonalized =>
        iif(() => isPersonalized,
          combineLatest([this.personalService.activeBusinessPartner$, this.appState.isPersonalDeliveryFilter$],
            (_, isPersonalFilterActive) => isPersonalFilterActive),
          of(false))
      ),
      // Fehleranzeige: Zugriff auf Personalisierungsdaten
      catchError(() => {
        return of(false);
      })
    ).subscribe(isPersonalizedView => {
      this.deliveryDescriptionsByDateCategory$ = this.deliveryService.getDeliveryDescriptionsByDateCategory(isPersonalizedView).pipe(
        catchError(err => {
          this.errorInfoForDeliveries$.next(this.errorHandlingService.toErrorInfo(err));
          return of();
        })
      );

    });

    this.pageState = this.pageStateService.pageState as DeliveryAllItemsPageState;
    if (this.pageState.isPersonalFilter !== undefined) {
      this.appState.isPersonalDeliveryFilter = this.pageState.isPersonalFilter;
    } else {
      this.pageState.isPersonalFilter = this.appState.isPersonalDeliveryFilter;
    }
  }

  ngOnDestroy(): void {
    this.personalizeSubscription?.unsubscribe();
    this.routeSubscription?.unsubscribe();
    this.personalizeNewsSubscription?.unsubscribe();
    this.loginSubscription?.unsubscribe();
  }

  private displayUserNameFromAuth(): void {
    this.auth.getUserInfo().then((userInfo: UserInfo) => {
      if (!!userInfo && userInfo.name.length > 0 ) {
        this.displayWelcome = this.welcomePersonal + `${userInfo.name}`;
      } else {
        this.displayWelcome = this.welcomeGeneral;
      }
    }).catch(() => {
      this.displayWelcome = this.welcomeGeneral;
    });
  }

  checkRouteForNewsItemsId(): void {
    const newsItemId = this.route.firstChild?.snapshot?.paramMap?.get('id') ?? undefined;
    if (!!newsItemId && this.dialog.openDialogs.length === 0) {
      const dialogConfig: MatDialogConfig = {
        // Dialog Beschriftung
        ariaLabel: 'wichtige Information',
        // Setzt die von Angular Material definerte maximale Breite zurück.
        maxWidth: '',
        // `w-full` für 100 % Breite sowie `max-w-**` um die maximale Breite zu beschränken.
        panelClass: ['w-full', '!max-w-4xl'],
        data: { newsItemId: newsItemId }
      };
      const dialogRef = this.dialog.open(AnnouncementComponent, dialogConfig);

      dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
        this.router.navigate(['delivery/all-items']);
      });
    }
  }

  getNewsItems(businessPartnerFilterIsActive: boolean): Observable<NewsItem[]> {
    return this.newsService.getNewsItems(businessPartnerFilterIsActive)
      .pipe(
        map((items: NewsItem[]) => items.filter((item: NewsItem) => !!item.id)),
        catchError(err => {
          if (businessPartnerFilterIsActive) {
            return this.newsService.getNewsItems(false).pipe(
              map((items: NewsItem[]) => items.filter((item: NewsItem) => !!item.id)),
              catchError(secondErr => {
                this.errorInfoForNews$.next(this.errorHandlingService.toErrorInfo(secondErr));
                throw err;
              })
            );
          }
          this.errorInfoForNews$.next(this.errorHandlingService.toErrorInfo(err));
          throw err;
        })
      );
  }

  changePersonalFilterState(event: MatSlideToggleChange): void {
    const isPersonalFilter = event.checked;
    this.appState.isPersonalDeliveryFilter = isPersonalFilter;
    this.adobeTrackingService.publishClickEvent('UpdateTermine:PersonalFilter:Toggle' +
      (isPersonalFilter ? 'Meine' : 'Alle'));
    this.pageState.isPersonalFilter = isPersonalFilter;
  }

  onPanelOpened(panelId: string): void {
    this.pageState.expandedPanelId = panelId;
  }

  linkToAnnualPlanning(): void {
    this.adobeTrackingService.publishClickEvent('Update-Termine:Alle Update-Termine:Jahresplanung.ics');

    window.location.href = this.translate.instant('delivery-all-items.annual_planning_url');
  }

  onClickCard(newsItem: NewsItem): void {
    // Adobe Analytics Einbindung
    this.adobeTrackingService.publishClickEvent('Home:Prominent:DetailClick');

    this.newsService.setLatestNewsItem(newsItem);
  }

  isElementVerticalOverflow(elementId: string): boolean {
    const htmlElement = document.getElementById(elementId);
    const clientHeight = htmlElement?.clientHeight;
    const scrollHeight = htmlElement?.scrollHeight;

    return !!(clientHeight && scrollHeight && scrollHeight > clientHeight);
  }

  getTeaserLineClampValue(elementId: string, itemScope: boolean): string {
    const bigHeader = this.isHeaderTwoLines(elementId) ? 1 : 0;
    const bigChangeCategory = this.isChangeCategoriesTwoLines(elementId) ? 1 : 0;

    switch (bigHeader + bigChangeCategory) {
      case 0: return itemScope ? 'line-clamp-4' : 'line-clamp-6';
      case 2: return itemScope ? 'line-clamp-2' : 'line-clamp-4';

      case 1:
      default:
        return itemScope ? 'line-clamp-3' : 'line-clamp-5';
    }
  }

  getTeaserHeightValue(elementId: string, itemScope: boolean): string {
    const bigHeader = this.isHeaderTwoLines(elementId) ? 1 : 0;
    const bigChangeCategory = this.isChangeCategoriesTwoLines(elementId) ? 1 : 0;

    switch (bigHeader + bigChangeCategory) {
      case 0: return itemScope ? 'h-24' : 'h-36';
      case 2: return itemScope ? 'h-12' : 'h-24';

      case 1:
      default:
        return itemScope ? 'h-18' : 'h-30';
    }
  }

  isHeaderTwoLines(elementId: string): boolean {
    const heightOneLine = 24;
    const element = document.getElementById(elementId);
    const clientHeight = element?.clientHeight;

    return !!clientHeight && clientHeight > heightOneLine;
  }

  isChangeCategoriesTwoLines(elementId: string): boolean {
    const heightOneLine = 24;
    const element = document.getElementById('changeCat_' + elementId.split('_')[1]);
    const clientHeight = element?.clientHeight;

    return !!clientHeight && clientHeight > heightOneLine;
  }

  isTeaserLineCutOff(elementId: string, parentElementId: string): boolean {
    const element = document.getElementById(elementId);
    const parentElement = document.getElementById(parentElementId);

    if (element && parentElement) {
      const elementOffsetBottom = element.offsetTop + element.offsetHeight;
      const parentElementOffsetBottom = parentElement.offsetTop + parentElement.offsetHeight;

      if (element.offsetTop < parentElementOffsetBottom && elementOffsetBottom > parentElementOffsetBottom) {
        return true;
      }
    }

    return false;
  }

}
